import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';

import hero1 from '../assets/images/hero1.jpg';
import hero2 from '../assets/images/hero2.jpg';
import hero3 from '../assets/images/hero3.jpg';
import story from '../assets/images/story.svg';

import site from '../content/site';
import services from '../content/services';
import work from '../content/work';
import shots from '../assets/workShots';
import { byPath } from '../content/routes';
import useSeo from '../seo/useSeo';
import { graphFor, service as serviceSchema } from '../seo/schema';

import Reveal from '../components/ui/Reveal';
import Section from '../components/ui/Section';
import SectionHeading from '../components/ui/SectionHeading';
import StatCounter from '../components/ui/StatCounter';
import ServiceCard from '../components/ui/ServiceCard';
import CTASection from '../components/ui/CTASection';

const slides = [
  {
    img: hero1,
    title: 'Software that earns its place',
    description:
      'We build the systems businesses depend on, from Calgary to wherever you are, and we say so honestly when you don’t need one.',
    alt: 'Codelithic Labs engineers collaborating on a custom software build.',
  },
  {
    img: hero2,
    title: 'A partner, not a vendor',
    description:
      'Senior people who learn your business, work in your tools and stay accountable after launch.',
    alt: 'A business team reviewing a technology strategy with Codelithic Labs consultants.',
  },
  {
    img: hero3,
    title: 'Built to still work in three years',
    description:
      'Tested, documented and handed over properly, so you’re never locked in to whoever wrote it.',
    alt: 'Scalable cloud infrastructure powering a Codelithic Labs software solution.',
  },
];

const audiences = [
  {
    name: 'Founders and startups',
    desc: 'Scope it down, get it in front of users, keep the code clean enough to hand over.',
    to: '/industries/startups-and-founders',
  },
  {
    name: 'Established businesses',
    desc: 'Integration, internal tools and security for teams whose systems have outgrown themselves.',
    to: '/industries',
  },
];

const differentiators = [
  'Senior engineers on your project, not juniors behind a sales team',
  'Fixed scope and honest estimates, revised in writing when things change',
  'You own the code, the infrastructure and the accounts from day one',
  'Straight answers, including when the answer is "don’t build this"',
];

const Home = () => {
  const route = byPath('/');
  const reduce = useReducedMotion();
  const [current, setCurrent] = useState(0);

  useSeo({
    ...route,
    schema: graphFor({
      path: '/',
      title: route.title,
      description: route.description,
      extra: services.map(serviceSchema),
    }),
  });

  useEffect(() => {
    if (reduce) return undefined;
    const id = setInterval(() => setCurrent((p) => (p + 1) % slides.length), 6000);
    return () => clearInterval(id);
  }, [reduce]);

  return (
    <>
      {/* ---------------- Hero ---------------- */}
      <section className="relative h-[100svh] min-h-[36rem] w-full overflow-hidden bg-primary" aria-label="Introduction">
        {/* The single, stable h1. The rotating headlines below are h2s. */}
        <h1 className="sr-only">
          Codelithic Labs — software development, cloud and IT consulting in Calgary
        </h1>

        {/* No mode="wait" here on purpose: both slides stay mounted through the
            transition so the new image fades in on top of the old one. Waiting
            would unmount the old image first and flash the bare background. */}
        <AnimatePresence>
          <motion.div
            key={current}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0.2 : 0.9 }}
            className="absolute inset-0"
          >
            <motion.img
              src={slides[current].img}
              alt={slides[current].alt}
              width="2400"
              height="750"
              fetchPriority={current === 0 ? 'high' : 'low'}
              loading={current === 0 ? 'eager' : 'lazy'}
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover"
              initial={{ scale: reduce ? 1 : 1.06 }}
              animate={{ scale: 1 }}
              transition={{ duration: 7, ease: 'linear' }}
            />
            {/* Two layers: a flat wash guarantees a contrast floor everywhere,
                the left-weighted gradient guarantees it specifically under the
                copy while still letting the right of the photo show through. */}
            <div className="absolute inset-0 bg-primary-900/30" />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-900/85 via-primary-900/45 to-transparent" />
          </motion.div>
        </AnimatePresence>

        <div className="container-page relative z-10 flex h-full flex-col justify-center">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="eyebrow"
            >
              <span className="h-px w-6 bg-secondary" aria-hidden="true" />
              Calgary, Alberta &middot; working worldwide
            </motion.span>

            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, y: reduce ? 0 : 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: reduce ? 0 : -16 }}
                transition={{ duration: reduce ? 0.15 : 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <h2 className="mt-5 text-4xl font-bold text-white sm:text-5xl md:text-6xl lg:text-7xl">
                  {slides[current].title}
                </h2>
                <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/75 md:text-xl">
                  {slides[current].description}
                </p>
              </motion.div>
            </AnimatePresence>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 flex flex-col gap-4 sm:flex-row"
            >
              <Link to="/contact" className="btn-primary btn-lg group">
                Start a conversation
                <ArrowRight size={18} aria-hidden="true" className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/services" className="btn-ghost btn-lg">
                Explore our services
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Slide controls */}
        <div className="absolute inset-x-0 bottom-8 z-20 flex justify-center gap-3">
          {slides.map((slide, i) => (
            <button
              key={slide.title}
              type="button"
              onClick={() => setCurrent(i)}
              aria-label={`Show slide ${i + 1}: ${slide.title}`}
              aria-current={current === i}
              /* Slim bar visually, but a 44px-tall hit area. An 8px dot
                 is not reliably tappable with a thumb. */
              className="group grid h-11 place-items-center px-1.5"
            >
              <span
                className={`block h-2 rounded-full transition-all duration-300 ${
                  current === i ? 'w-10 bg-secondary' : 'w-2 bg-white/40 group-hover:bg-white/70'
                }`}
              />
            </button>
          ))}
        </div>
      </section>

      {/* ---------------- Stats ---------------- */}
      <section className="bg-primary-700 py-14 md:py-16" aria-label="Codelithic Labs by the numbers">
        <div className="container-page grid grid-cols-2 gap-8 md:grid-cols-4">
          {site.stats.map((s, i) => (
            <StatCounter key={s.label} {...s} delay={i * 0.1} />
          ))}
        </div>
      </section>

      {/* ---------------- Services ---------------- */}
      <Section tone="white" labelledBy="home-services">
        <div className="container-page">
          <SectionHeading
            id="home-services"
            eyebrow="What we do"
            title="Eight services, one accountable team"
            subtitle="Whether you are testing a first idea or fixing systems that have outgrown themselves. Most clients start with one service and grow into several."
          />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((s, i) => (
              <ServiceCard key={s.slug} service={s} index={i} />
            ))}
          </div>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 sm:gap-6">
            {audiences.map((a, i) => (
              <Reveal key={a.name} delay={i * 0.08}>
                <Link
                  to={a.to}
                  className="group flex h-full items-start gap-4 rounded-3xl bg-light p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-soft sm:p-7"
                >
                  <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-secondary/10 text-secondary">
                    <ArrowRight size={16} aria-hidden="true" className="transition-transform group-hover:translate-x-0.5" />
                  </span>
                  <span>
                    <span className="block font-display text-lg font-bold text-primary transition-colors group-hover:text-secondary">
                      {a.name}
                    </span>
                    <span className="mt-1.5 block leading-relaxed text-accent">{a.desc}</span>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* ---------------- Selected work ---------------- */}
      <Section tone="light" labelledBy="home-work">
        <div className="container-page">
          <SectionHeading
            id="home-work"
            eyebrow="Selected work"
            title="Live projects, not mockups"
            subtitle="Every one of these is running in production. Open them and use them yourself."
          />
          <div className="grid gap-5 sm:gap-6 md:grid-cols-3">
            {work.map((w, i) => (
              <Reveal key={w.slug} delay={i * 0.08} className="h-full">
                <Link
                  to={`/work/${w.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-soft transition-all duration-300 ease-spring hover:-translate-y-1.5 hover:shadow-lift"
                >
                  <div className="overflow-hidden">
                    <img
                      src={shots[w.shot].desktop}
                      alt={`The ${w.name} website built by Codelithic Labs`}
                      width="1200"
                      height="750"
                      loading="lazy"
                      decoding="async"
                      className="h-40 w-full object-cover object-top transition-transform duration-500 ease-spring group-hover:scale-105 sm:h-44"
                    />
                  </div>
                  <div className="flex flex-grow flex-col p-6 sm:p-7">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-secondary">
                      {w.sector}
                    </span>
                    <h3 className="mt-2.5 font-display text-lg font-bold text-primary transition-colors group-hover:text-secondary sm:text-xl">
                      {w.name}
                    </h3>
                    <p className="mt-2 flex-grow leading-relaxed text-accent">{w.tagline}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.25}>
            <div className="mt-11 text-center">
              <Link to="/work" className="btn-outline btn-md group">
                See all our work
                <ArrowRight size={17} aria-hidden="true" className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* ---------------- Why us ---------------- */}
      <Section tone="light" labelledBy="home-why">
        <div className="container-page grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <div className="overflow-hidden rounded-4xl bg-white p-8 shadow-soft">
              <img
                src={story}
                alt="Illustration of the Codelithic Labs team working together."
                width="600"
                height="400"
                loading="lazy"
                decoding="async"
                className="h-auto w-full"
              />
            </div>
          </Reveal>

          <div>
            <SectionHeading
              id="home-why"
              align="left"
              eyebrow="Why Codelithic"
              title="The difference is who does the work"
              subtitle="Plenty of agencies sell you a senior team and staff the project with whoever is free. We don’t run that way."
            />
            <ul className="space-y-4">
              {differentiators.map((d, i) => (
                <Reveal as="li" key={d} delay={i * 0.08} className="flex items-start gap-3.5">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-secondary/10">
                    <Check size={14} className="text-secondary" strokeWidth={3} aria-hidden="true" />
                  </span>
                  <span className="leading-relaxed text-accent">{d}</span>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={0.3}>
              <Link to="/about" className="btn-outline btn-md mt-10 group">
                More about us
                <ArrowRight size={17} aria-hidden="true" className="transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* ---------------- Process ---------------- */}
      <Section tone="white" labelledBy="home-process">
        <div className="container-page">
          <SectionHeading
            id="home-process"
            eyebrow="How we work"
            title="Four stages, no surprises"
            subtitle="You see working software early and often, and you always know what the next two weeks look like."
          />
          <ol className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {site.process.map((p, i) => (
              <Reveal as="li" key={p.step} delay={i * 0.1}>
                <div className="group relative h-full rounded-3xl border border-primary/[0.07] bg-light p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-soft">
                  <span className="font-display text-5xl font-bold text-primary/10 transition-colors group-hover:text-secondary/25">
                    {p.step}
                  </span>
                  <h3 className="mt-3 font-display text-xl font-bold text-primary">{p.title}</h3>
                  <p className="mt-2.5 leading-relaxed text-accent">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </ol>
          <Reveal delay={0.2}>
            <div className="mt-12 text-center">
              <Link to="/process" className="btn-outline btn-md group">
                See the full process
                <ArrowRight size={17} aria-hidden="true" className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </Section>

      <CTASection />
    </>
  );
};

export default Home;
